import { RowCrudStatus } from "../../common/types";
import {
  User,
  UserCreateMutationVariables,
  UserUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { roleDisplayId } from "../../role/store/RoleCommonStore";
import { Language } from "../../../api/graphql";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { getFileVariableValue } from "../../common/utils";
import { tenantDisplayId } from "../../tenant/store/TenantCommonStore";
import { blankToNull } from "../../../common/blankToNull";
import { IdCardType } from "../../../api/graphql";
import { Gender } from "../../../api/graphql";

let uniqueCounter = 1;
export type UserValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  email: string;
  __role: string;
  showRole: { displayId: string; id: string } | null;
  language: Language;
  avatar: string;
  avatar_data: FileCRUDType;
  __avatar_files: FileCRUDType[];
  __targetTenant: string;
  showTargetTenant: { displayId: string; id: string } | null;
  tenantDisplayName: string;
  active: boolean;
  cellphone: string;
  twoFaseAuth: boolean;
  idCardType: IdCardType | "";
  idCard: string;
  gender: Gender | "";
};

export type UserErrorsType = {
  name?: { message: string; fieldLabel: string };
  email?: { message: string; fieldLabel: string };
  role?: { message: string; fieldLabel: string };
  language?: { message: string; fieldLabel: string };
  avatar?: { message: string; fieldLabel: string };
  targetTenant?: { message: string; fieldLabel: string };
  tenantDisplayName?: { message: string; fieldLabel: string };
  active?: { message: string; fieldLabel: string };
  cellphone?: { message: string; fieldLabel: string };
  twoFaseAuth?: { message: string; fieldLabel: string };
  idCardType?: { message: string; fieldLabel: string };
  idCard?: { message: string; fieldLabel: string };
  gender?: { message: string; fieldLabel: string };
};

export function UserValuesInit(): UserValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    email: "",
    __role: "",
    showRole: null,
    language: "es" as Language,
    avatar: "",
    avatar_data: fileCRUDInit(),
    __avatar_files: [],
    __targetTenant: "",
    showTargetTenant: null,
    tenantDisplayName: "",
    active: false,
    cellphone: "",
    twoFaseAuth: false,
    idCardType: "cuitCuil" as IdCardType,
    idCard: "",
    gender: "" as Gender | "",
  };
}

export type UserType = User | UserValuesType;

export function userDisplayId(source: Partial<User>, _langStore: LangStore) {
  return (
    (source.email ?? _langStore.emptyStringSymbol) +
    " " +
    (source.name ?? _langStore.emptyStringSymbol)
  );
}

export function getUserValues(
  source: UserType,
  _langStore: LangStore,
  _duplicated: boolean = false
): UserValuesType {
  const entityValues = UserValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : userDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.email = source.email;
  entityValues.showRole =
    "role" in source && source.role
      ? {
          id: source.role.id,
          displayId: roleDisplayId(source.role, _langStore),
        }
      : "showRole" in source && source.showRole
      ? { id: source.showRole.id, displayId: source.showRole.displayId }
      : null;
  entityValues.__role = entityValues.showRole?.displayId || "";
  entityValues.language = source.language;
  entityValues.__avatar_files =
    source.avatar_data && !_duplicated
      ? [
          {
            id:
              "id" in source.avatar_data && source.avatar_data.id
                ? source.avatar_data.id
                : `generated_${uniqueCounter++}`,
            name: source.avatar_data.name,
            description: source.avatar_data.description || undefined,
            size: source.avatar_data.size || 0,
            formattedSize: source.avatar_data.size
              ? formatBytes(source.avatar_data.size)
              : "",
            mimetype: source.avatar_data.mimetype,
            url: source.avatar_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.avatar = source.avatar && !_duplicated ? source.avatar : "";
  entityValues.avatar_data =
    source.avatar_data && "__status__" in source.avatar_data
      ? source.avatar_data
      : source.avatar_data
      ? {
          id: ("id" in source.avatar_data && source.avatar_data.id
            ? source.avatar_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.avatar_data.name,
          description: source.avatar_data.description || undefined,
          size: source.avatar_data.size || 0,
          formattedSize: source.avatar_data.size
            ? formatBytes(source.avatar_data.size)
            : "",
          mimetype: source.avatar_data.mimetype,
          url: source.avatar_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.showTargetTenant =
    "targetTenant" in source && source.targetTenant
      ? {
          id: source.targetTenant.id,
          displayId: tenantDisplayId(source.targetTenant, _langStore),
        }
      : "showTargetTenant" in source && source.showTargetTenant
      ? {
          id: source.showTargetTenant.id,
          displayId: source.showTargetTenant.displayId,
        }
      : null;
  entityValues.__targetTenant = entityValues.showTargetTenant?.displayId || "";
  entityValues.tenantDisplayName = source.tenantDisplayName || "";
  entityValues.active = source.active || false;
  entityValues.cellphone = source.cellphone || "";
  entityValues.twoFaseAuth = source.twoFaseAuth || false;
  entityValues.idCardType = source.idCardType || ("" as IdCardType | "");
  entityValues.idCard = source.idCard || "";
  entityValues.gender = source.gender || ("" as Gender | "");
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getUserUpdateVariables(
  values: UserValuesType
): UserUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    email: values.email,
    roleId: values.showRole?.id || "",
    language: values.language,
    avatar: getFileVariableValue(values.__avatar_files),
    active: values.active,
    cellphone: blankToNull(values.cellphone),
    twoFaseAuth: values.twoFaseAuth === undefined ? null : values.twoFaseAuth,
    idCardType: values.idCardType || null,
    idCard: blankToNull(values.idCard),
    gender: values.gender || null,
  };
}

export function getUserCreateVariables(
  values: UserValuesType
): UserCreateMutationVariables {
  return {
    name: values.name,
    email: values.email,
    roleId: values.showRole?.id || "",
    language: values.language,
    avatar: getFileVariableValue(values.__avatar_files),
    targetTenantId: values.showTargetTenant?.id || "",
    active: values.active,
    cellphone: blankToNull(values.cellphone),
    twoFaseAuth: values.twoFaseAuth === undefined ? null : values.twoFaseAuth,
    idCardType: values.idCardType || null,
    idCard: blankToNull(values.idCard),
    gender: values.gender || null,
  };
}
